<template>
  <div>
    <div v-if="isLoading">
      <div class="flex items-center justify-center w-full h-screen -mt-32">
        <spinner />
      </div>
    </div>
    <!--table-->
    <div
      v-else-if="!hasNoTransactions"
      class="table-wrapper w-full overflow-x-auto"
    >
      <el-table
        class="relative cursor-pointer font-normal text-root-2 text-xs lg:text-sm"
        max-height="500"
        style="width: 100%"
        :data="tableData"
        @row-click="openCustomerTransactionDetailsDrawer"
      >
        <el-table-column :width="breakpoints.smAndDown ? '200' : 'auto'">
          <template slot="header">
            <div
              class="flex items-center justify-between px-2 lg:px-5 font-semibold"
            >
              <p class="text-root-2 text-xs lg:text-sm whitespace-nowrap">
                Transaction Type
              </p>
            </div>
          </template>
          <template slot-scope="scope">
            <div class="flex items-center px-2 lg:px-5 gap-x-3">
              <send-money-icon v-if="isTransactionTransfer" />
              <arrow-down v-else :transactionType="transactionType" />
              <p
                style="word-break: normal"
                class="text-root-4 font-normal capitalize text-xs lg:text-sm flex gap-x-4"
              >
                <span>
                  {{ formatServerName(scope.row.type) || '----' }}
                </span>
              </p>
            </div>
          </template>
        </el-table-column>
        <el-table-column v-if="isTransactionTransfer">
          <template slot="header">
            <p
              style="word-break: normal"
              class="text-root-2 text-xs lg:text-sm"
            >
              Recipient Name
            </p>
          </template>
          <template slot-scope="scope">
            <p
              class="text-root-2 text-xs lg:text-sm font-normal px-2 lg:px-5"
              style="word-break: normal"
            >
              {{ scope.row.name || '--' }}
            </p>
          </template>
        </el-table-column>
        <el-table-column width="200">
          <template slot="header">
            <div class="flex items-center px-2 lg:px-5 font-semibold">
              <p
                style="word-break: normal"
                class="text-root-2 text-xs lg:text-sm"
              >
                Amount
              </p>
              <el-tooltip content="Sort" placement="top" effect="light">
                <div class="ml-2">
                  <sort-icon :sort-direction="sortDirection.amount" />
                </div>
              </el-tooltip>
            </div>
          </template>
          <template slot-scope="scope">
            <div>
              <p
                class="text-root-2 text-xs lg:text-sm font-semibold px-2 lg:px-5 overflow-x-scroll"
                style="word-break: normal"
              >
                {{ parseMoneyAll(scope.row.amount, scope.row.currency) }}
              </p>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="200">
          <template slot="header">
            <div class="font-semibold flex items-center lg:pl-5">
              <p
                style="word-break: normal"
                class="text-root-2 text-center text-xs lg:text-sm"
              >
                Status
              </p>
            </div>
          </template>
          <template slot-scope="scope">
            <div class="text-xs flex items-start font-normal ml-4 capitalize">
              <div
                :class="statusClass(scope.row.status)"
                class="py-1.5 px-3 rounded-lg flex items-center gap-1"
              >
                {{ formatServerName(scope.row.status) }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column>
          <template slot="header">
            <div class="flex items-center lg:px-5 font-semibold">
              <p
                style="word-break: normal"
                class="text-root-2 text-xs lg:text-sm"
              >
                Date Created
              </p>
              <el-tooltip content="Sort" placement="top" effect="light">
                <div class="ml-2">
                  <sort-icon :sortDirection="sortDirection" />
                </div>
              </el-tooltip>
            </div>
          </template>
          <template slot-scope="scope">
            <div
              class="flex items-center justify-between py-2 px-2 lg:px-5 font-normal text-xs lg:text-sm"
            >
              <el-tooltip
                :content="tooltipDateContent(scope.row.createdAt)"
                placement="top"
                effect="light"
              >
                <span class="capitalize">{{
                  parseRelativeTime(scope.row.createdAt)
                }}</span>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="50">
          <template slot="header"> </template>
          <template slot-scope="">
            <arrow-right-icon />
          </template>
        </el-table-column>
      </el-table>
      <div
        class="flex flex-col md:flex-row gap-2 justify-between md:items-center pt-4"
      >
        <pagination-details
          :page-size="page.size"
          :current-page="page.page"
          :total-count="page.totalCount"
        />
        <el-pagination
          @current-change="handleCurrentChange"
          @prev-click="previous"
          @next-click="next"
          :page-size="page.size"
          :current-page="page.page"
          :total="page.totalCount"
          layout="prev, pager, next"
          hide-on-single-page
          background
        >
        </el-pagination>
      </div>
    </div>
    <transaction-empty-state v-else>
      <h3 class="font-semibold text-lg lg:text-xl my-1.5">
        No {{ transactionType }} transaction
      </h3>
      <p class="font-light text-xs lg:text-sm md:text-base">
        Your {{ transactionType }} transactions shows here
      </p>
    </transaction-empty-state>
    <!-- drawer -->
    <div>
      <customer-transaction-drawer
        v-if="showTransactionDrawer"
        :showDrawer="showTransactionDrawer"
        :transactionDetails="transactionDetails"
        :transactionType="transactionType"
        @closeDrawer="closeTransactionDetailsDrawer"
      />
    </div>
  </div>
</template>
<script>
import {
  GetCurrencyCountry,
  StatusClass,
  FormatServerName,
  ParseMoneyAll,
  ParseRelativeTime
} from '@/utils/helpers.js'
import breakpoints from '@/utils/breakpoints.js'
import app from '@/mixins/app.js'

export default {
  mixins: [app],
  props: {
    tableData: {
      type: Array,
      default: () => []
    },
    transactionType: {
      type: String,
      default: () => ''
    },
    page: {
      type: Object,
      default: () => {}
    },
    isLoading: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      breakpoints,
      sortDirection: 'up',
      showTransactionDrawer: false,
      transactionDetails: {}
    }
  },
  methods: {
    parseRelativeTime (time) {
      return ParseRelativeTime(time)
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    statusClass (status) {
      return StatusClass(status)
    },
    formatServerName (val) {
      return FormatServerName(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    openCustomerTransactionDetailsDrawer (row) {
      this.transactionDetails = row
      this.showTransactionDrawer = true
    },
    closeTransactionDetailsDrawer () {
      this.showTransactionDrawer = false
    },
    handleCurrentChange (val) {
      this.payload.page = val
    },
    next () {
      this.payload.page++
    },
    previous () {
      this.payload.page--
    }
  },
  components: {
    Spinner: () => import('@/components/v2/app/Spinner.vue'),
    SendMoneyIcon: () => import('@/assets/v2/icons/SendMoneyIcon.vue'),
    ArrowDown: () => import('@/assets/v2/icons/ArrowDown.vue'),
    SortIcon: () => import('@/assets/v2/icons/SortIcon.vue'),
    ArrowRightIcon: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    PaginationDetails: () =>
      import('@/components/v2/app/PaginationDetails.vue'),
    CustomerTransactionDrawer: () =>
      import(
        '@/components/v2/externalApi/drawers/customerTransactionDrawer/index.vue'
      ),
    TransactionEmptyState: () =>
      import('@/components/v2/bulkPayout/layouts/TransactionEmptyState.vue')
  },
  computed: {
    isTransactionTransfer () {
      return this.transactionType === this.$transactionType.TRANSFER
    },
    hasNoTransactions () {
      return this.tableData.length === 0
    }
  }
}
</script>
<style scoped>
>>> .el-tooltip__popper.is-dark,
>>> .el-tooltip__popper.is-light,
>>> .el-tooltip__popper {
  background: white !important;
}

>>> .el-table {
  padding: 0;
  background: transparent;
  border-collapse: separate;
}

>>> .el-table table {
  border-spacing: 0 16px;
}

>>> .el-table th {
  border: none !important;
  background: #f7f7f7;
  padding: 0;
}

>>> .el-table tr {
  background: white;
  border-radius: 8px;
}
>>> .el-table tr:hover {
  cursor: pointer;
}
>>> .el-table td {
  border-bottom: none !important;
  background: white;
}

>>> .el-table td:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}
>>> .el-table td:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

>>> .el-table::before {
  height: 0px;
}
>>> .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background: white !important;
}
>>> .el-pagination.is-background .btn-next.disabled,
.el-pagination.is-background .btn-next:disabled,
.el-pagination.is-background .btn-prev.disabled,
.el-pagination.is-background .btn-prev:disabled,
.el-pagination.is-background .el-pager li.disabled {
  background: green !important;
}
>>> .el-pagination.is-background .btn-next,
.el-pagination.is-background .btn-prev,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}
>>> .el-pagination.is-background .btn-prev,
.el-pagination.is-background .btn-next,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}
>>> .el-input--prefix .el-input__inner {
  border: 1px solid var(--border-1);
  border-radius: 8px;
  background: var(--background-10);
}
>>> .el-tooltip__popper.is-light {
  background: white !important;
}
>>> .el-table__empty-text {
  display: none;
  height: 0;
}

>>> .el-table__empty-block {
  height: 0 !important;
  min-height: 0;
}
</style>
